import { getAuthInstance } from '@/lib/firebase'
import { savePostSigninRoute } from '@/router/signin'
import type { LocationQuery, LocationQueryRaw, Router, RouteRecordRaw, RouterScrollBehavior } from 'vue-router'

export const routes = [
  {
    path: '/index.html',
    redirect: '/',
  },
  {
    path: '/',
    name: 'landing',
    component: () => import('@/views/landing/LandingView.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/solutions/physical-therapy',
    name: 'landing-physical-therapy',
    component: () => import('@/views/landing/LandingPTView.vue'),
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import('@/views/landing/FAQView.vue'),
  },
  {
    path: '/pricing',
    name: 'pricing',
    component: () => import('@/views/landing/PricingView.vue'),
  },
  {
    path: '/integrations',
    name: 'integrations',
    component: () => import('@/views/landing/IntegrationsView.vue'),
  },
  {
    path: '/demo',
    name: 'demo',
    component: () => import('@/views/landing/DemoView.vue'),
  },
  {
    path: '/signin',
    children: [
      {
        path: '',
        name: 'signin',
        component: () => import('@/views/auth/SigninView.vue'),
        meta: { guestOnly: true },
      },
      {
        path: 'magic-link',
        name: 'magic-link',
        component: () => import('@/views/auth/MagicLinkView.vue'),
        meta: { guestOnly: true },
      },
      {
        path: 'microsoft',
        name: 'microsoft-signin',
        component: () => import('@/views/auth/AuthorizeMicrosoft.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'oidc',
        name: 'oidc-signin',
        component: () => import('@/views/auth/AuthorizeOIDC.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: '/signout',
    name: 'signout',
    component: () => import('@/views/auth/SignoutView.vue'),
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('@/views/AccountView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/account/cleanup',
    name: 'account-cleanup',
    component: () => import('@/views/AccountCleanupView.vue'),
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { requiresAuth: true, privacy: true },
  },
  {
    path: '/activity',
    name: 'activity',
    component: () => import('@/views/ActivityView.vue'),
    meta: { requiresAuth: true, privacy: true },
  },
  {
    path: '/journal',
    children: [
      {
        path: '',
        name: 'journal',
        component: () => import('@/views/JournalView.vue'),
        meta: { requiresAuth: true, privacy: true },
      },
      {
        path: 'print',
        name: 'journal-print',
        component: () => import('@/views/PrintJournalView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: ':id',
        name: 'journal-entry',
        component: () => import('@/views/JournalEntryView.vue'),
        meta: { requiresAuth: true, privacy: true },
      },
    ],
  },

  {
    path: '/tasks',
    name: 'tasks',
    component: () => import('@/views/TasksView.vue'),
    meta: { requiresAuth: true, privacy: true },
  },
  {
    path: '/clarifive',
    name: 'clarifive',
    component: () => import('@/views/ClarifiveView.vue'),
    meta: { requiresAuth: true, privacy: true },
  },
  {
    // The hash in the path below is used to obscure the labs feature and prevent accidental discovery.
    path: '/labs/cd0a1c00da5fbf73',
    name: 'labs',
    component: () => import('@/views/LabsView.vue'),
    meta: { requiresAuth: true, privacy: true },
  },
  {
    path: '/calendar',
    redirect: { name: 'account' },
  },
  {
    path: '/people',
    children: [
      {
        path: '',
        name: 'people',
        component: () => import('@/views/PeopleView.vue'),
        meta: { requiresAuth: true, privacy: true },
      },
      {
        path: ':id',
        name: 'person',
        component: () => import('@/views/PersonView.vue'),
        meta: { requiresAuth: true, privacy: true },
      },
    ],
  },
  {
    path: '/person/:id',
    redirect: (to) => ({ name: 'person', params: { id: to.params.id } }),
  },
  {
    path: '/protips',
    children: [
      {
        path: '',
        name: 'protips',
        component: () => import('@/views/protips/ProTipsListView.vue'),
      },
      {
        path: 'books',
        name: 'protips-books',
        component: () => import('@/views/protips/ProTipsBookListView.vue'),
      },
      {
        path: 'books/:id',
        name: 'protips-book',
        component: () => import('@/views/protips/ProTipBookView.vue'),
      },
      {
        path: 's/:section',
        name: 'protips-section',
        component: () => import('@/views/protips/ProTipSectionView.vue'),
      },
      {
        path: ':id',
        name: 'protip',
        component: () => import('@/views/protips/ProTipArticleView.vue'),
      },
    ],
  },
  {
    path: '/blog',
    children: [
      {
        path: '',
        name: 'blog',
        component: () => import('@/views/blog/BlogListView.vue'),
      },
      {
        path: ':id',
        name: 'blog-article',
        component: () => import('@/views/blog/BlogArticleView.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*', // Catch-all route for unmatched paths
    name: 'not-found',
    component: () => import('@/views/NotFound.vue'),
  },
] as RouteRecordRaw[]

function preserveUtmQuery(query: LocationQuery): LocationQueryRaw | undefined {
  const utmEntries = Object.entries(query).filter(([key]) => key.startsWith('utm_'))
  if (utmEntries.length === 0) {
    return undefined
  }
  return Object.fromEntries(utmEntries)
}

export const scrollBehavior: RouterScrollBehavior = (_to, _from, savedPosition) => {
  if (savedPosition) {
    return savedPosition
  }
  return { top: 0 }
}

export function setupRouter(router: Router) {
  if (import.meta.env.SSR && !import.meta.env.VITEST) return
  const auth = getAuthInstance()

  router.beforeEach(async (to, _from) => {
    await auth.authStateReady()
    const isAuthenticated = !!auth.currentUser

    if (to.meta.requiresAuth && !isAuthenticated) {
      savePostSigninRoute(to.fullPath)
      return { name: 'signin', query: preserveUtmQuery(to.query) }
    } else if (to.meta.guestOnly && isAuthenticated) {
      return { name: 'home' }
    }
    return
  })
}
